import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box, Button, CircularProgress, FormControl, FormControlLabel,
  InputLabel, MenuItem, RadioGroup, Radio, Select, TextField, Typography,
  Alert, Paper, Grid, InputAdornment, Chip, IconButton, Table, TableBody,
  TableCell, TableHead, TableRow, Divider, Stack,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon    from '@mui/icons-material/DeleteOutline';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import ExploreIcon from '@mui/icons-material/Explore';
import PersonIcon from '@mui/icons-material/Person';
import Brightness3Icon from '@mui/icons-material/Brightness3';
import BuildIcon from '@mui/icons-material/Build';
import SpeedIcon from '@mui/icons-material/Speed';
import PowerIcon from '@mui/icons-material/Power';
import BarChartIcon from '@mui/icons-material/BarChart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import {
  getHsp, getHspIbge, getEquipamentosOffGrid,
} from './api';
import { cores } from '../styles/styleUtils';
import usePerformanceRatio from '../hooks/usePerformanceRatio';
import PerformanceRatioInfo from '../components/PerformanceRatioInfo';

// ─── Constantes ───────────────────────────────────────────────────────────────
const DIAS_MES         = 30;
const TARIFA           = 1.12;

const MESES_FULL  = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const CHAVES_HSP  = ['hspJan','hspFev','hspMar','hspAbr','hspMai','hspJun','hspJul','hspAgo','hspSet','hspOut','hspNov','hspDez'];

const TIPOS_CLIENTE = [
  { label: 'Residencial',       pctNoturno: 0.46 },
  { label: 'Comercial',         pctNoturno: 0.37 },
  { label: 'Industrial',        pctNoturno: 0.31 },
  { label: 'Poder Público',     pctNoturno: 0.43 },
  { label: 'Rural',             pctNoturno: 0.47 },
  { label: 'Rural - Irrigante', pctNoturno: 0.40 },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getFatorOrientacao(orientacao) {
  if (!orientacao?.nome) return 1.0;
  const nome = orientacao.nome;
  if (nome.includes('Leste') || nome.includes('Oeste')) return 0.85;
  if (nome.includes('Sul') || nome.includes('Sudoeste') || nome.includes('Sudeste')) return 0.70;
  return 1.00;
}

function calcEnergiaTotal(potencia, qtde, horas) {
  return (Number(potencia) || 0) * (Number(qtde) || 0) * (Number(horas) || 0);
}

function calcSomaDiaria(equipamentos) {
  return equipamentos.reduce((acc, eq) => acc + calcEnergiaTotal(eq.potencia, eq.qtde, eq.horas), 0);
}

export function calcPotenciaCC(mediaMensal, hspMedio, fatorOrientacao, pr) {
  const denom = hspMedio * DIAS_MES * pr * fatorOrientacao;
  if (!denom) return 0;
  return mediaMensal / denom;
}

// ─── Componente ───────────────────────────────────────────────────────────────
const DimensionamentoOffGrid = ({ proposta, onDimensionado, onVoltar }) => {
  const estadoPR = usePerformanceRatio(proposta, 'offgrid');
  const { pr } = estadoPR;

  // ── HSP ───────────────────────────────────────────────────────────────────
  const [hsp,            setHsp]            = useState(null);
  const [loadingHsp,     setLoadingHsp]     = useState(false);
  const [erroHsp,        setErroHsp]        = useState('');
  const [dod,            setDod]            = useState(0.9);
  const [rte,            setRte]            = useState(0.9);

  // ── Equipamentos ──────────────────────────────────────────────────────────
  const [equipBiblioteca, setEquipBiblioteca] = useState([]);
  const [equipamentos,    setEquipamentos]    = useState([
    { id: Date.now(), nome: '', potencia: '', qtde: 1, horas: '', outros: false },
  ]);

  // ── Tipo de cliente ───────────────────────────────────────────────────────
  const [tipoCliente,      setTipoCliente]      = useState(TIPOS_CLIENTE[0]);
  const [modoCalculo,      setModoCalculo]      = useState('padrao');
  const [pctNoturnoCustom, setPctNoturnoCustom] = useState('');

  // ── Resultado do cálculo ──────────────────────────────────────────────────
  const [resultado, setResultado] = useState(null); // null = ainda não calculou

  // ── Carrega HSP ───────────────────────────────────────────────────────────
  useEffect(() => {
    const ibge   = proposta?.localinstalacao?.cidade?.id;
    const cidade = proposta?.localinstalacao?.cidade?.nome;
    const uf     = proposta?.localinstalacao?.estado?.sigla;
    if (!ibge && !cidade) return;
    setLoadingHsp(true);
    setErroHsp('');
    const buscar = async () => {
      let res = null;
      if (ibge) { try { res = await getHspIbge(Number(ibge)); } catch(e) { /* fallback */ } }
      if (!res && cidade && uf) { try { res = await getHsp(cidade, uf); } catch(e) { /* não encontrado */ } }
      if (res) setHsp(res);
      else setErroHsp('HSP não encontrado para a cidade. Verifique o local de instalação.');
      setLoadingHsp(false);
    };
    buscar();
  }, [proposta?.localinstalacao?.cidade?.id, proposta?.localinstalacao?.cidade?.nome]);

  // ── Carrega biblioteca de equipamentos e índices ──────────────────────────
  useEffect(() => {
    const carregar = async () => {
      try {
        const equips = await getEquipamentosOffGrid();
        setEquipBiblioteca(Array.isArray(equips) ? equips : []);
      } catch(e) { /* usa padrões */ }
    };
    carregar();
  }, []);

  useEffect(() => {
    const indices = estadoPR.indices;
    if (!Array.isArray(indices)) return;
    const dodI = indices.find(i => i.nome === 'DoD (Off-Grid)');
    const rteI = indices.find(i => i.nome === 'RoundTrip Efficiency (Off-Grid)');
    if (dodI?.valor) setDod(dodI.valor);
    if (rteI?.valor) setRte(rteI.valor);
  }, [estadoPR.indices]);

  // ── Cálculos base ─────────────────────────────────────────────────────────
  const somaDiaria  = calcSomaDiaria(equipamentos);
  const mediaMensal = somaDiaria * DIAS_MES;
  const pctNoturno  = modoCalculo === 'padrao'
    ? tipoCliente.pctNoturno
    : (Number(pctNoturnoCustom) / 100 || 0);
  const autonomia   = somaDiaria * pctNoturno;
  const hspMedio    = hsp ? CHAVES_HSP.reduce((s, k) => s + (hsp[k] || 0), 0) / 12 : 0;
  const fatorOrient = getFatorOrientacao(proposta?.orientacaotelhado);
  const potenciaCC  = hspMedio > 0 ? calcPotenciaCC(mediaMensal, hspMedio, fatorOrient, pr) : 0;

  // ── Handlers de equipamentos ──────────────────────────────────────────────
  const addEquipamento = () => setEquipamentos(prev => [
    ...prev, { id: Date.now(), nome: '', potencia: '', qtde: 1, horas: '', outros: false },
  ]);

  const removeEquipamento = (id) => setEquipamentos(prev => prev.filter(e => e.id !== id));

  const updateEquipamento = (id, field, value) =>
    setEquipamentos(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));

  // ── Calcular Dimensionamento → gera tabela mensal ─────────────────────────
  const handleCalcular = () => {
    if (!estadoPR.pronto) { setErroHsp(estadoPR.erro || 'Aguarde o carregamento do PR.'); return; }
    if (!hsp) { setErroHsp('Aguarde o carregamento do HSP.'); return; }
    if (equipamentos.every(eq => !eq.nome && !eq.potencia)) {
      setErroHsp('Adicione ao menos um equipamento.'); return;
    }
    setErroHsp('');

    // Gera tabela mensal de geração × economia
    const meses = CHAVES_HSP.map((chave, i) => {
      const hspMes   = hsp[chave] || 0;
      const producao = Number((potenciaCC * hspMes * DIAS_MES * pr * fatorOrient).toFixed(2));
      const economia = Number((producao * TARIFA).toFixed(2));
      return { mes: MESES_FULL[i], hsp: hspMes, producao, economia };
    });

    setResultado({
      pr,
      equipamentos,
      somaDiaria:     Number(somaDiaria.toFixed(4)),
      mediaMensal:    Number(mediaMensal.toFixed(2)),
      autonomia:      Number(autonomia.toFixed(4)),
      pctNoturno,
      tipoCliente:    tipoCliente.label,
      modoCalculo,
      potenciaCC:     Number(potenciaCC.toFixed(3)),
      hspMedio:       Number(hspMedio.toFixed(2)),
      hsp,
      fatorOrientacao: fatorOrient,
      dod,
      rte,
      meses,
    });
  };

  // ── Confirmar e Continuar → passa dados para o pai ───────────────────────
  const handleConfirmar = () => {
    if (!resultado) return;
    onDimensionado?.(resultado);
  };

  const cidadeNome  = proposta?.localinstalacao?.cidade?.nome || '–';
  const estadoSigla = proposta?.localinstalacao?.estado?.sigla || '';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" gap={1} mb={1}>
        <BatteryChargingFullIcon sx={{ color: cores.laranja }} />
        <Typography variant="h5" fontWeight={700} sx={{ color: cores.textoClaro }}>
          Dimensionamento Off-Grid
        </Typography>
      </Stack>

      {/* Localização + HSP */}
      <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
        <Typography variant="body2" sx={{ color: cores.textoClaro, opacity: 0.75 }}>
          Local de instalação: <strong>{cidadeNome}{estadoSigla ? ` / ${estadoSigla}` : ''}</strong>
        </Typography>
        {loadingHsp && <CircularProgress size={12} />}
        {hsp && <Chip icon={<WbSunnyIcon />} label={`HSP médio: ${hspMedio.toFixed(2)} h/dia`} size="small" color="success" />}
        <Chip
          icon={<ExploreIcon />}
          label={`Orientação: ${proposta?.orientacaotelhado?.nome || 'Norte'} (×${fatorOrient})`}
          size="small" color="info" variant="outlined"
        />
      </Box>

      <PerformanceRatioInfo estado={estadoPR} />
      {erroHsp && <Alert severity="error" sx={{ mb: 2 }}>{erroHsp}</Alert>}

      {/* Perfil do Cliente */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <PersonIcon fontSize="small" sx={{ color: cores.laranja }} />
          <Typography variant="subtitle1" fontWeight={600}>Perfil do Cliente</Typography>
        </Stack>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de Cliente</InputLabel>
              <Select
                value={tipoCliente.label}
                label="Tipo de Cliente"
                startAdornment={(
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" sx={{ color: cores.laranja }} />
                  </InputAdornment>
                )}
                onChange={(e) => {
                  const tc = TIPOS_CLIENTE.find(t => t.label === e.target.value);
                  if (tc) { setTipoCliente(tc); setResultado(null); }
                }}
              >
                {TIPOS_CLIENTE.map(tc => (
                  <MenuItem key={tc.label} value={tc.label}>
                    {tc.label} — {(tc.pctNoturno * 100).toFixed(0)}% noturno
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl>
              <RadioGroup row value={modoCalculo} onChange={(e) => { setModoCalculo(e.target.value); setResultado(null); }}>
                <FormControlLabel value="padrao" control={<Radio size="small" />} label="Cálculo padrão" />
                <FormControlLabel value="personalizado" control={<Radio size="small" />} label="Personalizado" />
              </RadioGroup>
            </FormControl>
            {modoCalculo === 'personalizado' && (
              <TextField
                size="small" label="% Consumo Noturno" type="number"
                value={pctNoturnoCustom}
                onChange={e => { setPctNoturnoCustom(e.target.value); setResultado(null); }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ mt: 1, maxWidth: 200 }}
              />
            )}
          </Grid>
        </Grid>
        <Box mt={1.5} display="flex" gap={1} flexWrap="wrap">
          <Chip icon={<Brightness3Icon />} label={`Consumo noturno: ${(pctNoturno * 100).toFixed(0)}%`} size="small" color="warning" variant="outlined" />
          <Chip icon={<WbSunnyIcon />} label={`Consumo diurno: ${((1 - pctNoturno) * 100).toFixed(0)}%`} size="small" color="primary" variant="outlined" />
        </Box>
      </Paper>

      {/* Equipamentos */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Stack direction="row" alignItems="center" gap={1}>
            <BuildIcon fontSize="small" sx={{ color: cores.laranja }} />
            <Typography variant="subtitle1" fontWeight={600}>Equipamentos</Typography>
          </Stack>
          <Button startIcon={<AddCircleOutlineIcon />} variant="outlined" size="small"
            onClick={() => { addEquipamento(); setResultado(null); }}>
            Adicionar Equipamento
          </Button>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: '#f5f5f5' }}>
                <TableCell sx={{ minWidth: 220 }}>Equipamento</TableCell>
                <TableCell sx={{ minWidth: 130 }}>Pot. Individual (kW)</TableCell>
                <TableCell sx={{ minWidth: 80 }}>Qtde</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Horas de Uso (h/dia)</TableCell>
                <TableCell sx={{ minWidth: 130 }}>Energia Total (kWh/dia)</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {equipamentos.map((eq) => (
                <TableRow key={eq.id}>
                  <TableCell>
                    <Select
                      displayEmpty size="small" fullWidth
                      value={eq.outros ? '__outros__' : eq.nome}
                      onChange={(e) => {
                        const val = e.target.value;
                        setResultado(null);
                        if (val === '__outros__') {
                          updateEquipamento(eq.id, 'outros', true);
                          updateEquipamento(eq.id, 'nome', '');
                          updateEquipamento(eq.id, 'potencia', '');
                        } else {
                          const sel = equipBiblioteca.find(b => b.nome === val);
                          if (sel) {
                            setEquipamentos(prev => prev.map(item => item.id === eq.id
                              ? { ...item, nome: sel.nome, potencia: sel.potencia, outros: false }
                              : item
                            ));
                          }
                        }
                      }}
                      renderValue={(v) => {
                        if (eq.outros) return eq.nome || <em style={{ color: '#aaa' }}>Digite o equipamento...</em>;
                        return v || <em style={{ color: '#aaa' }}>Selecione...</em>;
                      }}
                    >
                      <MenuItem value=""><em>— Selecione da biblioteca —</em></MenuItem>
                      {equipBiblioteca.map(b => (
                        <MenuItem key={b._id} value={b.nome}>{b.nome} ({b.potencia} kW)</MenuItem>
                      ))}
                      <Divider />
                      <MenuItem value="__outros__"><em>Outros (digitar manualmente)</em></MenuItem>
                    </Select>
                    {eq.outros && (
                      <TextField
                        size="small" fullWidth placeholder="Nome do equipamento"
                        value={eq.nome}
                        onChange={e => { updateEquipamento(eq.id, 'nome', e.target.value); setResultado(null); }}
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small" type="number" fullWidth
                      value={eq.potencia}
                      onChange={e => { updateEquipamento(eq.id, 'potencia', e.target.value); setResultado(null); }}
                      placeholder={eq.outros ? 'kW' : undefined}
                      InputProps={{ endAdornment: <InputAdornment position="end">kW</InputAdornment> }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" fullWidth value={eq.qtde}
                      onChange={e => { updateEquipamento(eq.id, 'qtde', e.target.value); setResultado(null); }} />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small" type="number" fullWidth value={eq.horas}
                      onChange={e => { updateEquipamento(eq.id, 'horas', e.target.value); setResultado(null); }}
                      InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {calcEnergiaTotal(eq.potencia, eq.qtde, eq.horas).toFixed(3)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" color="error"
                      onClick={() => { removeEquipamento(eq.id); setResultado(null); }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* Totais */}
        <Box mt={2} display="flex" gap={2} flexWrap="wrap">
          <Chip icon={<SpeedIcon />} label={`Média de Consumo: ${mediaMensal.toFixed(2)} kWh/mês`} color="primary" />
          <Chip icon={<BatteryChargingFullIcon />} label={`Autonomia Necessária: ${autonomia.toFixed(2)} kWh/dia`} color="warning" />
          {hsp && <Chip icon={<PowerIcon />} label={`Potência CC Estimada: ${potenciaCC.toFixed(2)} kWp`} color="success" />}
        </Box>
      </Paper>

      {/* Tabela de Geração Mensal (aparece após calcular) */}
      {resultado && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Stack direction="row" alignItems="center" gap={1} mb={1}>
            <BarChartIcon fontSize="small" sx={{ color: cores.laranja }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Geração Estimada por Mês
            </Typography>
          </Stack>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#f5f5f5' }}>
                  <TableCell><strong>Mês</strong></TableCell>
                  <TableCell align="center"><strong>HSP (h/dia)</strong></TableCell>
                  <TableCell align="center"><strong>Produção (kWh/mês)</strong></TableCell>
                  <TableCell align="right"><strong>Economia (R$/mês)</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resultado.meses.map((m) => (
                  <TableRow key={m.mes} sx={{ '&:hover': { backgroundColor: 'Azure' } }}>
                    <TableCell>{m.mes}</TableCell>
                    <TableCell align="center">{m.hsp.toFixed(4)}</TableCell>
                    <TableCell align="center">{m.producao.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      {m.economia.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Box mt={2} display="flex" gap={2} flexWrap="wrap">
            <Chip
              icon={<WbSunnyIcon />}
              label={`Produção Média Mensal: ${(resultado.meses.reduce((s, m) => s + m.producao, 0) / 12).toFixed(2)} kWh`}
              color="success"
            />
            <Chip
              icon={<AttachMoneyIcon />}
              label={`Economia Média Mensal: ${(resultado.meses.reduce((s, m) => s + m.economia, 0) / 12).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}
              color="primary"
            />
          </Box>
        </Paper>
      )}

      {/* Navegação */}
      <Box display="flex" gap={2}>
        {onVoltar && (
          <Button variant="outlined" color="error" onClick={onVoltar} startIcon={<ArrowBackIcon />}>Voltar</Button>
        )}
        {!resultado ? (
          <Button
            variant="contained" color="primary"
            onClick={handleCalcular}
            disabled={loadingHsp || !hsp || somaDiaria === 0 || !estadoPR.pronto}
          >
            Calcular Dimensionamento
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              sx={{ color: cores.textoClaro, borderColor: cores.textoClaro }}
              startIcon={<RefreshIcon />}
              onClick={() => setResultado(null)}
            >
              Recalcular
            </Button>
            <Button
              variant="contained"
              color="success"
              endIcon={<CheckCircleIcon />}
              onClick={handleConfirmar}
            >
              Confirmar e Continuar
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default DimensionamentoOffGrid;
